// ---- The firestart minigame (spark a campfire) ------------------------------
// WASD/arrow lane twin of the chop/reel: prompts fall toward a hit line;
// press the matching key in the window. Progress = prompts cleared; a fail
// meter rises on misses. Materials spend is owned by the activity/server.
//
// Import discipline: sim/firestarting.ts + types.ts only — NO game/net/state.

import {
  startFireJudge, applyFireResult,
  type FirePattern, type FireStartTuning, type FireJudge,
} from "../sim/firestarting.ts";
import type { Minigame } from "./types.ts";

const LANE_KEYS = ["w", "a", "s", "d"] as const;
const LANE_GLYPHS = ["▲", "◀", "▼", "▶"] as const;
const ALIAS: Record<string, number> = {
  w: 0, a: 1, s: 2, d: 3,
  ArrowUp: 0, ArrowLeft: 1, ArrowDown: 2, ArrowRight: 3,
};

const LANE_H = 240;
const PX_PER_SEC = 150;
const HIT_Y = LANE_H - 28;

export type FireGameEvents = {
  /** `finished` = this input resolved the pattern (lit or failed). */
  onInput(i: number, hit: boolean, finished: boolean): void;
};

export function makeFireStartMinigame(cfg: {
  pattern: FirePattern;
  hitWindow: number;
  tun: Pick<FireStartTuning, "missTension" | "hitRelief">;
  ev: FireGameEvents;
  title?: string;
  hintLabel?: () => string;
}): Minigame & { setHintLabel(s: string): void } {
  const { pattern, hitWindow, tun, ev } = cfg;
  const title = cfg.title ?? "🔥 SPARK — match the keys!";
  let root: HTMLElement | null = null;
  let tensionFill: HTMLElement | null = null;
  let progressEl: HTMLElement | null = null;
  let hintEl: HTMLElement | null = null;
  let arrowEls: HTMLElement[] = [];
  let laneEls: HTMLElement[] = [];
  let judge: FireJudge = startFireJudge(pattern, hitWindow);
  let elapsed = 0;
  let done = false;
  let hintText = cfg.hintLabel?.() ?? "";

  const flashLane = (lane: number, ok: boolean) => {
    const el = laneEls[lane];
    if (!el) return;
    el.style.background = ok ? "rgba(120,220,140,0.28)" : "rgba(230,90,80,0.28)";
    setTimeout(() => { el.style.background = "rgba(10,18,26,0.55)"; }, 120);
  };

  const judgeNext = (hit: boolean) => {
    if (done || !root) return;
    const i = judge.idx;
    const evs = applyFireResult(judge, hit, tun);
    const el = arrowEls[i];
    if (el) {
      el.style.opacity = "0.25";
      el.style.color = hit ? "#7ee29a" : "#e66a5a";
    }
    const finished = evs.length > 0;
    if (finished) done = true;
    ev.onInput(i, hit, finished);
  };

  return {
    setHintLabel(s: string) {
      hintText = s;
      if (hintEl) hintEl.textContent = s;
    },

    mount() {
      if (root) return;
      root = document.createElement("div");
      root.id = "minigame";
      root.style.cssText =
        "position:fixed;left:50%;bottom:120px;transform:translateX(-50%);z-index:60;" +
        "display:flex;flex-direction:column;align-items:center;gap:6px;pointer-events:none;" +
        "font-family:inherit;user-select:none;";

      const lanes = document.createElement("div");
      lanes.style.cssText = "display:flex;gap:8px;";
      laneEls = [];
      arrowEls = [];
      for (let lane = 0; lane < 4; lane++) {
        const laneEl = document.createElement("div");
        laneEl.style.cssText =
          `position:relative;width:52px;height:${LANE_H}px;overflow:hidden;border-radius:8px;` +
          "background:rgba(10,18,26,0.55);border:1px solid rgba(255,255,255,0.14);transition:background 80ms;";
        const line = document.createElement("div");
        line.style.cssText =
          `position:absolute;left:0;right:0;top:${HIT_Y}px;height:2px;background:rgba(255,255,255,0.6);`;
        const label = document.createElement("div");
        label.textContent = LANE_KEYS[lane]!.toUpperCase();
        label.style.cssText =
          `position:absolute;left:0;right:0;top:${HIT_Y + 4}px;text-align:center;` +
          "font-size:13px;font-weight:700;color:rgba(255,255,255,0.65);";
        laneEl.append(line, label);
        lanes.appendChild(laneEl);
        laneEls.push(laneEl);
      }
      for (let i = 0; i < pattern.arrows.length; i++) {
        const a = pattern.arrows[i]!;
        const el = document.createElement("div");
        el.textContent = LANE_GLYPHS[a.dir]!;
        el.style.cssText =
          "position:absolute;left:0;right:0;text-align:center;font-size:26px;line-height:1;" +
          "color:#ffb86a;text-shadow:0 1px 3px rgba(0,0,0,0.7);will-change:transform;transform:translateY(-40px);";
        laneEls[a.dir]!.appendChild(el);
        arrowEls.push(el);
      }

      const tension = document.createElement("div");
      tension.style.cssText =
        "width:232px;height:8px;border-radius:4px;background:rgba(10,18,26,0.6);" +
        "border:1px solid rgba(255,255,255,0.14);overflow:hidden;";
      tensionFill = document.createElement("div");
      tensionFill.style.cssText =
        "height:100%;width:0%;background:linear-gradient(90deg,#e6b45a,#e66a5a);transition:width 90ms;";
      tension.appendChild(tensionFill);

      progressEl = document.createElement("div");
      progressEl.style.cssText = "font-size:12px;color:rgba(255,255,255,0.8);text-shadow:0 1px 2px rgba(0,0,0,0.8);";
      progressEl.textContent = `spark 0 / ${pattern.arrows.length}`;

      hintEl = document.createElement("div");
      hintEl.style.cssText = "font-size:11px;color:rgba(255,200,120,0.9);text-shadow:0 1px 2px rgba(0,0,0,0.8);";
      hintEl.textContent = hintText;

      if (title) {
        const titleEl = document.createElement("div");
        titleEl.textContent = title;
        titleEl.style.cssText =
          "font:bold 15px ui-monospace,monospace;color:#ffb86a;white-space:nowrap;" +
          "background:rgba(0,0,0,0.67);padding:8px 16px;border-radius:20px;box-shadow:0 2px 10px #0006;";
        root.append(titleEl, lanes, tension, progressEl, hintEl);
      } else {
        root.append(lanes, tension, progressEl, hintEl);
      }
      document.body.appendChild(root);
      judge = startFireJudge(pattern, hitWindow);
      elapsed = 0;
      done = false;
    },

    tick(dt: number) {
      if (!root) return;
      elapsed += dt;
      while (
        root && !done &&
        judge.idx < pattern.arrows.length &&
        elapsed > pattern.arrows[judge.idx]!.t + hitWindow
      ) {
        flashLane(pattern.arrows[judge.idx]!.dir, false);
        judgeNext(false);
      }
      if (!root) return;
      for (let i = judge.idx; i < pattern.arrows.length; i++) {
        const a = pattern.arrows[i]!;
        const el = arrowEls[i];
        if (!el) continue;
        const y = HIT_Y - (a.t - elapsed) * PX_PER_SEC;
        el.style.transform = `translateY(${Math.round(y - 26)}px)`;
      }
      if (tensionFill) tensionFill.style.width = `${Math.min(100, judge.tension * 100)}%`;
      if (progressEl && !done) progressEl.textContent = `spark ${judge.idx} / ${pattern.arrows.length}`;
      if (progressEl && done) progressEl.textContent = judge.tension >= 1 ? "embers die…" : "it catches!";
      if (hintEl && cfg.hintLabel) hintEl.textContent = cfg.hintLabel();
    },

    key(e: KeyboardEvent): boolean {
      if (!root || done) return false;
      const dir = ALIAS[e.key.length === 1 ? e.key.toLowerCase() : e.key];
      if (dir === undefined) return false;
      const i = judge.idx;
      const a = pattern.arrows[i];
      if (a && a.dir === dir && Math.abs(elapsed - a.t) <= hitWindow) {
        flashLane(dir, true);
        judgeNext(true);
      } else {
        flashLane(dir, false);
        if (a) judgeNext(false);
      }
      return true;
    },

    unmount() {
      root?.remove();
      root = null;
      tensionFill = null;
      progressEl = null;
      hintEl = null;
      arrowEls = [];
      laneEls = [];
    },
  };
}
